/**
 * Workspace Path Conventions
 *
 * @module workflows/diagnostics/materialize/paths
 * @description Single source of truth for the relative paths inside a diagnostic workspace.
 * Every builder writes through these helpers; every reader (agent prompt, CLI subcommands)
 * uses these constants. Renaming a path is a one-file change.
 */

import * as path from 'node:path';

export const WORKSPACE_PATHS = {
  manifest: 'manifest.json',
  reportedFailure: 'reported-failure.md',
  summary: 'summary.md',
  ledger: 'ledger.md',
  ledgerIndex: 'ledger-index.md',

  steps: 'steps', // steps/<NNN>-<stepId>.md

  timeline: 'timeline',
  timelineSteps: 'timeline/steps.jsonl',
  timelineActions: 'timeline/actions.jsonl',
  timelineNavigation: 'timeline/navigation.jsonl',
  timelineAssertions: 'timeline/assertions.jsonl',
  timelineCacheDecisions: 'timeline/cache-decisions.jsonl',
  timelineStepDiagnostics: 'timeline/step-diagnostics.jsonl',

  thoughts: 'thoughts',
  thoughtsAll: 'thoughts/all.jsonl',
  thoughtsByStep: 'thoughts/by-step', // thoughts/by-step/<stepId>.md

  browser: 'browser',
  browserTraceZip: 'browser/trace.zip',
  browserActions: 'browser/actions.jsonl',
  browserDoms: 'browser/doms', // browser/doms/<NNNN>-<timestamp>.html
  browserFrames: 'browser/frames', // browser/frames/<NNNN>-<timestamp>.png
  browserA11y: 'browser/a11y', // browser/a11y/<NNNN>-<timestamp>.yaml
  browserResources: 'browser/resources',
  // Continuous screencast filmstrip — real pixels at every moment, including
  // evaluate/wait moments where there is no per-action DOM-snapshot frame.
  browserScreencast: 'browser/screencast', // browser/screencast/<NNNN>-<timestamp>.jpg
  browserScreencastIndex: 'browser/screencast/index.jsonl',

  logs: 'logs',
  logsNetwork: 'logs/network.jsonl',
  logsNetworkErrors: 'logs/network-errors.jsonl',
  logsConsole: 'logs/console.jsonl',
  logsConsoleErrors: 'logs/console-errors.jsonl',
  logsSentry: 'logs/sentry-logs.jsonl',
  logsSentrySpans: 'logs/sentry-spans.jsonl',
  logsSandbox: 'logs/sandbox.jsonl',
  logsSandboxMeta: 'logs/sandbox.meta.json',

  execution: 'execution',
  executionContext: 'execution/context.json',

  // Mutations a prior cache/L2 layer already committed (cache_replay:* / l2_playbook:*).
  committedActions: 'committed-actions.md',
  committedActionsJsonl: 'committed-actions.jsonl',

  workflow: 'workflow',
  workflowDefinition: 'workflow/definition.json',
  workflowSnapshot: 'workflow/snapshot.json',
  workflowNodes: 'workflow/nodes', // workflow/nodes/<nodeId>.json
  workflowPreflight: 'workflow/preflight.md', // written only when the workflow declares preflight

  variables: 'variables',
  variablesInputs: 'variables/inputs.json',
  variablesLineage: 'variables/lineage.jsonl',

  history: 'history',
  historyRecentRuns: 'history/recent-runs.jsonl',
  historyLastGreen: 'history/last-green', // CLI-only: `canary workflow debug materialize --out` target
  historyRuns: 'history/runs', // agent-only: history/runs/<runId>/ — comparison workspaces from materialize_run

  notes: 'notes',
} as const;

/** Subdirectories created up-front (idempotent) so builders can write files without mkdir-ing each time. */
export const WORKSPACE_DIRS: ReadonlyArray<string> = [
  WORKSPACE_PATHS.steps,
  WORKSPACE_PATHS.timeline,
  WORKSPACE_PATHS.thoughts,
  WORKSPACE_PATHS.thoughtsByStep,
  WORKSPACE_PATHS.browser,
  WORKSPACE_PATHS.browserDoms,
  WORKSPACE_PATHS.browserFrames,
  WORKSPACE_PATHS.browserA11y,
  WORKSPACE_PATHS.browserResources,
  WORKSPACE_PATHS.browserScreencast,
  WORKSPACE_PATHS.logs,
  WORKSPACE_PATHS.execution,
  WORKSPACE_PATHS.workflow,
  WORKSPACE_PATHS.workflowNodes,
  WORKSPACE_PATHS.variables,
  WORKSPACE_PATHS.history,
  WORKSPACE_PATHS.notes,
];

/** Resolve a workspace-relative path to an absolute path under the workspace root. */
export const resolveWorkspacePath = (root: string, relative: string): string =>
  path.join(root, relative);

/** Build the per-step rollup filename: `steps/003-<stepId>.md`. */
export const stepRollupRelativePath = (orderIndex: number, stepId: string): string =>
  path.join(WORKSPACE_PATHS.steps, `${String(orderIndex).padStart(3, '0')}-${stepId}.md`);

/** Build the per-step thoughts filename: `thoughts/by-step/<stepId>.md`. */
export const thoughtsByStepRelativePath = (stepId: string): string =>
  path.join(WORKSPACE_PATHS.thoughtsByStep, `${stepId}.md`);

/** Build the per-node config filename: `workflow/nodes/<nodeId>.json`. */
export const workflowNodeRelativePath = (nodeId: string): string =>
  path.join(WORKSPACE_PATHS.workflowNodes, `${nodeId}.json`);

/** Build the per-action artifact filename: `<NNNN>-<absoluteTsMs>.<ext>`. */
export const actionArtifactBasename = (
  actionIndex: number,
  absoluteTsMs: number,
  ext: 'html' | 'png' | 'yaml'
): string => `${String(actionIndex).padStart(4, '0')}-${absoluteTsMs}.${ext}`;

/** Build the per-frame screencast filename: `<NNNN>-<absoluteTsMs>.jpg`. */
export const screencastFrameBasename = (frameIndex: number, absoluteTsMs: number): string =>
  `${String(frameIndex).padStart(4, '0')}-${absoluteTsMs}.jpg`;
